function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import { getOr, mapValues, omit } from '@livechat/data-utils';
import deferred from '@livechat/deferred';
export var createActionCreator = function createActionCreator(type, actionCreator) {
  var wrappedActionCreator = function wrappedActionCreator() {
    var data = actionCreator.apply(void 0, arguments);
    return typeof data === 'function' ? wrappedActionCreator : {
      type: type,
      payload: data
    };
  }; // TODO: remove this getActionName


  wrappedActionCreator.getActionName = function () {
    return type;
  };

  wrappedActionCreator.toString = function () {
    return type;
  };

  return wrappedActionCreator;
};
export var createActionMethod = function createActionMethod(type, actionCreator) {
  var wrappedActionCreator = function wrappedActionCreator(store) {
    var proxyStore = Object.create(store);

    proxyStore.action = function (payload, meta) {
      var actionData = {
        type: type,
        payload: payload
      };

      if (meta) {
        actionData.meta = meta;
      }

      store.dispatch(actionData);
    };

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return actionCreator.apply(void 0, [proxyStore].concat(args));
  };

  wrappedActionCreator.toString = function () {
    return type;
  };

  return wrappedActionCreator;
};
export var createRequestableActionMethodPair = function createRequestableActionMethodPair(type, actionCreator) {
  var requestType = "REQUEST_" + type;
  var actionMethod = createActionMethod(type, actionCreator);

  var requestActionMethod = function requestActionMethod(store) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }

    var proxyStore = Object.create(store);
    var result;

    proxyStore.action = function (payload) {
      var _deferred = deferred(),
          promise = _deferred.promise,
          resolve = _deferred.resolve,
          reject = _deferred.reject;

      store.dispatch({
        type: requestType,
        payload: _extends({}, payload, {
          resolve: resolve,
          reject: reject
        })
      });
      result = promise.then(function () {
        actionMethod.apply(void 0, [store].concat(args));
      });
    };

    actionCreator.apply(void 0, [proxyStore].concat(args));

    if (typeof result === 'undefined') {
      throw new Error("You forgot to call `action` handler in " + requestType + " creator.");
    }

    return result;
  };

  requestActionMethod.toString = function () {
    return requestType;
  };

  return {
    actionMethod: actionMethod,
    requestActionMethod: requestActionMethod
  };
};

var bindActionMethod = function bindActionMethod(actionMethod, store) {
  return function () {
    for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
      args[_key3] = arguments[_key3];
    }

    return actionMethod.apply(void 0, [store].concat(args));
  };
};

export var bindActionMethods = function bindActionMethods(actionMethods, store) {
  if (typeof actionMethods !== 'object' || actionMethods === null || Array.isArray(actionMethods)) {
    throw new Error('bindActionMethods expects a plain object with actionMethods as values.');
  }

  return mapValues(function (actionMethod) {
    return bindActionMethod(actionMethod, store);
  }, actionMethods);
};
export var createReducer = function createReducer(initialState, reducersMap) {
  if (process.env.NODE_ENV !== 'development' && reducersMap[undefined]) {
    // eslint-disable-next-line no-console
    console.warn(["Reducer contains an 'undefined' action type.", 'Have you misspelled a constant?'].join('\n'));
  }

  return function reducer(state, action) {
    if (state === void 0) {
      state = initialState;
    }

    if (reducersMap.hasOwnProperty(action.type)) {
      return reducersMap[action.type](state, action.payload);
    }

    return state;
  };
}; // TODO: this is the same as built-in redux's combineReducers, should be remobed

export function combineReducers(reducers) {
  var reducerKeys = Object.keys(reducers);
  var finalReducers = {};

  for (var index = 0; index < reducerKeys.length; index++) {
    var key = reducerKeys[index];

    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key];
    }
  }

  var finalReducerKeys = Object.keys(finalReducers);
  return function combination(state, action) {
    if (state === void 0) {
      state = {};
    }

    var hasChanged = false;
    var nextState = {};

    for (var _index = 0; _index < finalReducerKeys.length; _index++) {
      var _key4 = finalReducerKeys[_index];
      var reducer = finalReducers[_key4];
      var previousStateForKey = state[_key4];
      var nextStateForKey = reducer(previousStateForKey, action);
      nextState[_key4] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }

    return hasChanged ? nextState : state;
  };
}
export var getOrIdentity = function getOrIdentity(path, state) {
  return getOr(state, path, state);
};

var capitalizeFirstLetter = function capitalizeFirstLetter(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export var createPropsReducer = function createPropsReducer(propName) {
  var _ref;

  return _ref = {}, _ref["SET_PROPERTY_" + propName.toUpperCase()] = function SET_PROPERTY_(state, payload) {
    var _extends2;

    return _extends({}, state, (_extends2 = {}, _extends2[propName] = payload.data, _extends2));
  }, _ref["REMOVE_PROPERTY_" + propName.toUpperCase()] = function REMOVE_PROPERTY_(state) {
    return _extends({}, omit([propName], state));
  }, _ref;
};
export var createPropsSelectors = function createPropsSelectors(propName) {
  var _ref2;

  return _ref2 = {}, _ref2["getProperty" + capitalizeFirstLetter(propName)] = function getProperty(state) {
    return state[propName];
  }, _ref2;
};
export var createPropsActions = function createPropsActions(propName) {
  var _ref3;

  return _ref3 = {}, _ref3["setProperty" + capitalizeFirstLetter(propName)] = createActionCreator("SET_PROPERTY_" + propName.toUpperCase(), function (data) {
    return {
      data: data
    };
  }), _ref3["removeProperty" + capitalizeFirstLetter(propName)] = createActionCreator("REMOVE_PROPERTY_" + propName.toUpperCase(), function () {
    return {
      propName: propName
    };
  }), _ref3;
};
export var createIndexedObjectReducer = function createIndexedObjectReducer(reducer) {
  return function (state, action) {
    if (state === void 0) {
      state = {};
    }

    var key = action.payload.id;
    var previousValueState = state[key];
    var nextValueState = reducer(state[key], action);

    if (nextValueState !== previousValueState) {
      var _extends3;

      return _extends({}, state, (_extends3 = {}, _extends3[key] = nextValueState, _extends3));
    }

    return state;
  };
};
export var thunkMiddleware = function thunkMiddleware(store) {
  return function (next) {
    return function (action) {
      if (typeof action === 'function') {
        return action(store);
      }

      return next(action);
    };
  };
};
export var storeAccessMiddleware = function storeAccessMiddleware(store) {
  return function (next) {
    return function (action) {
      return next(typeof action === 'function' ? action(store) : action);
    };
  };
};

var bindSelector = function bindSelector(selector, getState) {
  return function () {
    for (var _len4 = arguments.length, args = new Array(_len4), _key5 = 0; _key5 < _len4; _key5++) {
      args[_key5] = arguments[_key5];
    }

    return selector.apply(void 0, [getState()].concat(args));
  };
};

export var bindSelectors = function bindSelectors(selectors, getState) {
  if (typeof selectors !== 'object' || selectors === null || Array.isArray(selectors)) {
    throw new Error('bindSelectors expects a plain object with selectors as values.');
  }

  return mapValues(function (selector) {
    return bindSelector(selector, getState);
  }, selectors);
}; // https://github.com/acdlite/reduce-reducers/blob/c28b9e9544036c797d7b523da8b2612755dee1f3/src/index.js

export var reduceReducers = function reduceReducers() {
  for (var _len5 = arguments.length, reducers = new Array(_len5), _key6 = 0; _key6 < _len5; _key6++) {
    reducers[_key6] = arguments[_key6];
  }

  return function (previous, action) {
    return reducers.reduce(function (state, reducer) {
      return reducer(state, action);
    }, previous);
  };
};